// Medical Sidebar component with encrypted query parameter support
// Automatically appends encrypted orgId and appId to navigation routes
import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  IconButton,
  Tooltip,
  Chip,
} from "@mui/material";
import {
  Business as BusinessIcon,
  People as PeopleIcon,
  Science as ScienceIcon,
  Tune as TuneIcon,
  Inventory as InventoryIcon,
  Description as DescriptionIcon,
  Verified as VerifiedIcon,
  Close as CloseIcon,
  Send as SendIcon,
  Dashboard as DashboardIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Architecture as ArchitectureIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  encryptText,
  encryptAppId,
  decryptText,
  decryptAppId,
} from "../../utils/encryption";
import ApplicationCountdown from "../../components/ApplicationCountdown";
import PageTitle from "../../components/PageTitle";
import { TerminateApplicationDialog } from "../../components";

const Sidebar = ({
  isMobile,
  onClose,
  autoSaveProgress = 0,
  activeStep = 0,
  onSectionClick = () => { },
  showDeclaration = false, // New prop to control Declaration visibility
  showEnclosuresComplete = false, // New prop to control Final Submit visibility when all enclosures are complete
  collapsed = false, // New prop to control collapsed state
  onCollapseToggle = () => { }, // New prop to handle collapse toggle
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [applicationStage, setApplicationStage] = useState(null);
  const [assessmentTypeId, setAssessmentTypeId] = useState(null);
  const [terminateAppId, setTerminateAppId] = useState(null);
  const [assessmentTypeName, setAssessmentTypeName] = useState("");
  const [userTypeId, setUserTypeId] = useState(null);
  const [countdownData, setCountdownData] = useState(null);

  // Get current parameters for countdown
  const getCurrentParameters = useCallback(() => {
    // First, try to get decrypted parameters from URL query string
    const urlParams = new URLSearchParams(location.search);
    const encryptedOrgIdFromUrl = urlParams.get("orgId");
    const encryptedAppIdFromUrl = urlParams.get("appId");
    const encryptedAssessmentIdFromUrl = urlParams.get("assessmentTypeId");
    const capacity = urlParams.get("capacity");

    let orgId = null;
    let appId = null;
    let NewassessmentId = null;

    // Decrypt parameters from URL if available
    if (encryptedOrgIdFromUrl) {
      try {
        orgId = decryptText(encryptedOrgIdFromUrl);
      } catch (error) {
        console.error("Error decrypting orgId from URL:", error);
      }
    }
    if (encryptedAssessmentIdFromUrl) {
      try {
        NewassessmentId = decryptText(encryptedAssessmentIdFromUrl);
      } catch (error) {
        console.error("Error decrypting orgId from URL:", error);
      }
    }

    if (encryptedAppIdFromUrl) {
      try {
        appId = decryptAppId(encryptedAppIdFromUrl);
      } catch (error) {
        console.error("Error decrypting appId from URL:", error);
      }
    }

    // Fall back to localStorage if URL parameters are not available
    if (!orgId) {
      orgId = localStorage.getItem("orgId");
    }
    if (!appId) {
      appId = localStorage.getItem("appId");
    }

    const labTypeId = localStorage.getItem("labTypeId") || "3";

    return { orgId, appId, labTypeId };
  }, [location.search]);

  // Get application stage from API with encrypted query parameters
  const getApplicationStage = useCallback(
    async (appId) => {
      try {
        if (!appId) return;

        // First, get encrypted parameters from URL query string
        const urlParams = new URLSearchParams(location.search);
        const encryptedOrgIdFromUrl = urlParams.get("orgId");
        const encryptedAppIdFromUrl = urlParams.get("appId");

        // Decrypt the parameters from URL if available
        let decryptedOrgId = null;
        let decryptedAppId = null;

        if (encryptedOrgIdFromUrl) {
          try {
            decryptedOrgId = decryptText(encryptedOrgIdFromUrl);
            console.log("Sidebar - Decrypted orgId from URL:", decryptedOrgId);
          } catch (error) {
            console.error("Error decrypting orgId from URL:", error);
          }
        }

        if (encryptedAppIdFromUrl) {
          try {
            decryptedAppId = decryptAppId(encryptedAppIdFromUrl);
            console.log("Sidebar - Decrypted appId from URL:", decryptedAppId);
          } catch (error) {
            console.error("Error decrypting appId from URL:", error);
          }
        }

        // Fall back to localStorage if URL parameters are not available
        const orgId = decryptedOrgId || localStorage.getItem("orgId");
        const finalAppId = decryptedAppId || appId;
        const labTypeId = localStorage.getItem("labTypeId") || "3"; // Default to 3 for medical


        // Build query string with encrypted parameters for the API call
        const queryParams = new URLSearchParams();
        if (orgId) {
          const encryptedOrgId = encryptText(orgId);
          queryParams.append("orgId", encryptedOrgId);
        }
        if (labTypeId) {
          const encryptedLabTypeId = encryptText(labTypeId);
          queryParams.append("labTypeId", encryptedLabTypeId);
        }

        const queryString = queryParams.toString();
        const url = `${process.env.REACT_APP_API_URL || "http://localhost:5000/api"}/laboratory/application-stage/${finalAppId}`;

        console.log("🔍 Sidebar - Loading application stage with URL:", url);

        const response = await fetch(url);
        const result = await response.json();

        if (result.success) {
          setApplicationStage(result.stage);
          setAssessmentTypeId(result.assessmentTypeId);
          setAssessmentTypeName(result.assessmentTypeName);
          setTerminateAppId(finalAppId);
        } else {
          setApplicationStage("1"); // Default to 1
        }
      } catch (error) {
        console.error("Error fetching application stage:", error);
        setApplicationStage("1"); // Default to 1
      }
    },
    [location.search],
  );

  // Get application stage and user type
  useEffect(() => {
    const appId =
      localStorage.getItem("appId") || localStorage.getItem("orgId");
    const userType = localStorage.getItem("userTypeId") || "1";

    setUserTypeId(userType);

    if (appId) {
      getApplicationStage(appId);
    }
  }, [location.search, getApplicationStage]); // Add location.search as dependency to re-run when URL params change

  // Handle capacity changes when URL changes
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const urlCapacity = urlParams.get("capacity");
    const storedCapacity = localStorage.getItem("capacity");

    // If URL has capacity and it's different from stored capacity, update localStorage
    if (urlCapacity && urlCapacity !== storedCapacity) {
      console.log(
        "🔄 Sidebar - Updating capacity in localStorage from URL:",
        urlCapacity,
      );
      const decryptedcapacity = decryptText(urlCapacity);
      localStorage.setItem("capacity", decryptedcapacity);
    }

    // If URL doesn't have capacity but localStorage does, keep localStorage value
    // If neither URL nor localStorage has capacity, do nothing (no capacity restrictions)
  }, [location.search]);

  // Check if current user is a sub-user
  const isSubUser = localStorage.getItem("isSubUser") === "true";

  console.log("🔍 Sidebar - isSubUser check:", {
    isSubUser,
    isSubUserValue: localStorage.getItem("isSubUser"),
    userTypeId,
    disciplineId: localStorage.getItem("disciplineId"),
  });

  // Check if user should see final submit and dashboard options
  const shouldShowFinalSubmitOptions =
    (applicationStage === "18" || applicationStage === "18b") &&
    userTypeId === "4";
  const shouldShowFinaluserSubmitOptions =
    applicationStage === "18a" && showEnclosuresComplete;
  const shouldShowFinaluserSubmitOptionsadditional = (applicationStage === "18" || applicationStage === "18b") && showEnclosuresComplete && userTypeId === "4" && (assessmentTypeId === 13 || assessmentTypeId === 17 || assessmentTypeId === 11 || assessmentTypeId === 5 || assessmentTypeId === 12);
  const shouldShowFinaluserSubmitOptionsadditionalLab = applicationStage === "18a" && showEnclosuresComplete && userTypeId !== "4" && (assessmentTypeId === 13 || assessmentTypeId === 17 || assessmentTypeId === 11 || assessmentTypeId === 5 || assessmentTypeId === 12);
  const shouldshowassessmentMenu = userTypeId === "6";

  // Debug logging for Final Submit conditions
  console.log("🔍 Final Submit Debug Info:", {
    applicationStage,
    userTypeId,
    showEnclosuresComplete,
    shouldShowFinaluserSubmitOptions,
    medicalEnclosureCompleted: localStorage.getItem(
      "medicalEnclosureCompleted",
    ),
  });
  // Countdown data callback
  const handleCountdownDataLoaded = useCallback((data) => {
    setCountdownData(data);
    console.log("📅 Countdown data loaded in sidebar:", data);
  }, []);

  // Get current parameters for countdown
  const currentParams = getCurrentParameters();

  // Get and manage capacity with localStorage priority
  const getCapacity = useCallback(() => {
    // First, check if capacity exists in localStorage
    let capacity = localStorage.getItem("capacity");

    if (capacity) {
      console.log("🔍 Sidebar - Using capacity from localStorage:", capacity);
      return capacity;
    }

    // If not in localStorage, get from URL and set to localStorage
    const urlParams = new URLSearchParams(location.search);
    const urlCapacity = urlParams.get("capacity");

    if (urlCapacity) {
      console.log(
        "🔍 Sidebar - Found capacity in URL, setting to localStorage:",
        urlCapacity,
      );
      const decryptedcapacity = decryptText(urlCapacity);
      localStorage.setItem("capacity", decryptedcapacity);
      return decryptedcapacity;
    }

    // No capacity found anywhere
    console.log("🔍 Sidebar - No capacity found in localStorage or URL");
    return null;
  }, [location.search]);

  const capacity = getCapacity();
  console.log("🔍 Sidebar - Final capacity value:", capacity);

  // Function to clear capacity from localStorage (if needed)
  const clearCapacity = useCallback(() => {
    localStorage.removeItem("capacity");
    console.log("🗑️ Sidebar - Capacity cleared from localStorage");
  }, []);

  const MenuItems = [
    {
      title: "Laboratory Details (1)",
      icon: BusinessIcon,
      path: "/main/medical/laboratory-details",
      completed: false,
      id: "laboratory-details",
    },
    {
      title: "Laboratory Details",
      icon: BusinessIcon,
      path: "/main/medical/laboratory-detailsAssessment",
      completed: false,
      id: "laboratory-detailsAssessment",
      conditional: true,
      requiredUserType6: true,
    },
    {
      title: "Organization Chart (2)",
      icon: PeopleIcon,
      path: "/main/medical/organization",
      completed: false,
      id: "organization",
    },
    {
      title: "Organization Chart",
      icon: ScienceIcon,
      path: "/main/medical/organizationAssessment",
      completed: false,
      id: "organization-assessment",
      requiredUserType6: true,
    },
    {
      title: "Proficiency Testing (3)",
      icon: ScienceIcon,
      path: "/main/medical/proficiency-testing",
      completed: false,
      id: "proficiency-testing",
    },
    {
      title: "Discipline (4)",
      icon: TuneIcon,
      path: "/main/medical/discipline",
      completed: false,
      id: "discipline",
    },
    {
      title: "Discipline",
      icon: TuneIcon,
      path: "/main/medical/disciplineAssessment",
      completed: false,
      id: "disciplineAssessment",
      conditional: true,
      requiredUserType6: true,
    },
    {
      title: "Lab Staff (5)",
      icon: PeopleIcon,
      path: "/main/medical/lab-staff",
      completed: false,
      id: "lab-staff",
    },
    {
      title: "Lab Staff",
      icon: PeopleIcon,
      path: "/main/medical/lab-staff-additional",
      completed: false,
      id: "lab-staff-additional",
      conditional: true,
    },
    {
      title: "Lab Staff",
      icon: PeopleIcon,
      path: "/main/medical/lab-staffAssessment",
      completed: false,
      id: "lab-staffSectionAssessment",
      conditional: true,
      requiredUserType6: true,
    },
    // {
    //   title: 'Create Sub User',
    //   icon: PeopleIcon,
    //   path: '/main/medical/sub-user',
    //   completed: false,
    //   id: 'sub-user'
    // },
    {
      title: "Non Analytical Instruments (6)",
      icon: ArchitectureIcon,
      path: "/main/medical/non-analytical-instruments",
      completed: false,
      id: "non-analytical-instruments",
    },
    {
      title: "Fees (7)",
      icon: InventoryIcon,
      path: "/main/medical/fees",
      completed: false,
      id: "fees",
    },
    {
      title: "Enclosure (8)",
      icon: DescriptionIcon,
      path: "/main/medical/enclosure",
      completed: false,
      id: "enclosure",
    },
    {
      title: "Declaration (9)",
      icon: VerifiedIcon,
      path: "/main/medical/declaration",
      completed: false,
      id: "declaration",
      conditional: true, // This item shows conditionally
    },
    {
      title: "Declaration (9)",
      icon: VerifiedIcon,
      path: "/main/medical/declaration-additional",
      completed: false,
      id: "declaration-additional",
      conditional: true,
    },
    {
      title: "Final Submit",
      icon: SendIcon,
      path: "/main/medical/final-submit",
      completed: false,
      id: "final-submit-do",
      conditional: true,
      requiresStage18UserType4: true,
    },
    {
      title: "Final Submit",
      icon: SendIcon,
      path: "/main/medical/final-submit",
      completed: false,
      id: "final-submit-lab",
      conditional: true,
      requiresStage18a: true,
    },
    {
      title: "Back to Dashboard",
      icon: DashboardIcon,
      path: "/main/dashboard",
      completed: false,
      id: "back-to-dashboard",
      conditional: true,
      requiresStage18a: true,
    },
    {
      title: "Back to Dashboard",
      icon: DashboardIcon,
      path: "/main/do/dashboard",
      completed: false,
      id: "back-to-admin-dashboard",
      conditional: true,
      requiresStage18UserType4: true,
    },
    {
      title: "Final Submit",
      icon: SendIcon,
      path: "/main/medical/final-submit-additional",
      completed: false,
      id: "final-submit-additional",
      conditional: true,
      requiresStage18aadditional: true,
    },
  ];

  // Function to create encrypted query string for navigation
  const createEncryptedQueryString = () => {
    // First, try to get decrypted parameters from URL query string
    const urlParams = new URLSearchParams(location.search);
    const encryptedOrgIdFromUrl = urlParams.get("orgId");
    const encryptedAppIdFromUrl = urlParams.get("appId");
    const encryptedLabtypeIdFromUrl = urlParams.get("labType");
    const encryptedAssessmentIdFromUrl = urlParams.get("assessmentId");
    const encryptedAssessorIdFromUrl = urlParams.get("assessorId");
    const encryptedCapacityFromUrl = urlParams.get("capacity");

    let orgId = null;
    let appId = null;
    let labTypeId = null;
    let assessmentId = null;
    let assessorId = null;
    let capacity = null;

    // Decrypt parameters from URL if available
    if (encryptedOrgIdFromUrl) {
      try {
        orgId = decryptText(encryptedOrgIdFromUrl);
        console.log(
          "Sidebar - Using decrypted orgId from URL for navigation:",
          orgId,
        );
      } catch (error) {
        console.error("Error decrypting orgId for navigation:", error);
      }
    }

    if (encryptedAppIdFromUrl) {
      try {
        appId = decryptAppId(encryptedAppIdFromUrl);
        console.log(
          "Sidebar - Using decrypted appId from URL for navigation:",
          appId,
        );
      } catch (error) {
        console.error("Error decrypting appId for navigation:", error);
      }
    }
    if (encryptedLabtypeIdFromUrl) {
      try {
        labTypeId = decryptText(encryptedLabtypeIdFromUrl);
        console.log(
          "Sidebar - Using decrypted labTypeId from URL for navigation:",
          labTypeId,
        );
      } catch (error) {
        console.error("Error decrypting labTypeId for navigation:", error);
      }
    }
    if (encryptedAssessmentIdFromUrl) {
      try {
        assessmentId = decryptText(encryptedAssessmentIdFromUrl);
        console.log(
          "Sidebar - Using decrypted assessmentId from URL for navigation:",
          assessmentId,
        );
      } catch (error) {
        console.error("Error decrypting assessmentId for navigation:", error);
      }
    }
    if (encryptedAssessorIdFromUrl) {
      try {
        assessorId = decryptText(encryptedAssessorIdFromUrl);
        console.log(
          "Sidebar - Using decrypted assessorId from URL for navigation:",
          assessorId,
        );
      } catch (error) {
        console.error("Error decrypting assessorId for navigation:", error);
      }
    }
    if (encryptedCapacityFromUrl) {
      try {
        capacity = decryptText(encryptedCapacityFromUrl);
        console.log(
          "Sidebar - Using decrypted capacity from URL for navigation:",
          capacity,
        );
      } catch (error) {
        console.error("Error decrypting capacity for navigation:", error);
      }
    }

    // Fall back to localStorage if URL parameters are not available
    if (!orgId) {
      orgId = localStorage.getItem("orgId");
    }
    if (!appId) {
      appId = localStorage.getItem("appId");
    }

    if (!orgId && !appId) {
      return "";
    }

    const params = new URLSearchParams();

    if (orgId) {
      const encryptedOrgId = encryptText(orgId);
      params.append("orgId", encryptedOrgId);
    }

    if (appId) {
      const encryptedAppId = encryptAppId(appId);
      params.append("appId", encryptedAppId);
    }
    if (labTypeId) {
      const encryptedLabTypeId = encryptText(labTypeId);
      params.append("labTypeId", encryptedLabTypeId);
    }
    if (assessmentId) {
      const encryptedAssessmentId = encryptText(assessmentId);
      params.append("assessmentId", encryptedAssessmentId);
    }
    if (assessorId) {
      const encryptedAssessorId = encryptText(assessorId);
      params.append("assessorId", encryptedAssessorId);
    }
    if (capacity) {
      const encryptedCapacity = encryptText(capacity);
      params.append("capacity", encryptedCapacity);
    }
    if (assessmentTypeId) {
      const encryptedAssessmentTypeId = encryptText(assessmentTypeId);
      params.append("assessmentTypeId", encryptedAssessmentTypeId);
    }

    return params.toString() ? `?${params.toString()}` : "";
  };

  const handleSectionClick = (item, index) => {
    // Get current lab type from localStorage
    const labTypeId = localStorage.getItem("labTypeId") || "2";

    // Check if it's an external URL
    if (item.path.startsWith("http://") || item.path.startsWith("https://")) {
      // For external URLs, use window.location.href
      window.location.href = item.path;
    } else {
      // For internal routes, append encrypted query parameters
      const encryptedQueryString = createEncryptedQueryString();
      const fullPath = `${item.path}${encryptedQueryString}`;

      console.log("Sidebar - navigating to full path:", fullPath);

      // Use React Router navigate
      navigate(fullPath);
    }

    // Close mobile drawer if open
    if (isMobile) {
      onClose();
    }
  };

  return (
    <>
      <Box
        sx={{
          p: 2,
          borderBottom: "1px solid #e0e0e0",
          transition: "all 0.3s ease-in-out",
          backgroundColor: isSubUser ? "#e3f2fd" : "transparent", // Highlight for sub-users
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            transition: "all 0.3s ease-in-out",
          }}
        >
          {!collapsed ? (
            <Box
              sx={{
                opacity: collapsed ? 0 : 1,
                transition: "opacity 0.3s ease-in-out",
                flexGrow: 1,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "#00024c",
                  transition: "all 0.3s ease-in-out",
                }}
              >
                Medical Laboratory Application
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  transition: "all 0.3s ease-in-out",
                }}
              >
                {isSubUser
                  ? "Sub-User Access (Limited)"
                  : "Complete your application"}
              </Typography>
              <Chip
                variant="contained"
                size="small"
                sx={{
                  backgroundColor:
                    assessmentTypeId === 2 ? "#08be20ff" : "#2d7be0ff",
                  color: assessmentTypeId === 2 ? "#eaeee9ff" : "#f4f6f8ff",
                  border: "1px solid",
                  borderColor:
                    assessmentTypeId === 2 ? "#19d232ff" : "#0059fdff",
                  height: "auto",
                  "& .MuiChip-label": {
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    display: "block",
                    padding: "6px 8px",
                    lineHeight: "1.2",
                  },
                }}
                label={assessmentTypeName || ""}
              />
              {/* <TerminateApplicationDialog appId={terminateAppId} stage={applicationStage} /> */}
            </Box>
          ) : (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                transition: "all 0.3s ease-in-out",
              }}
            >
              {/* <DashboardIcon sx={{ 
                color: '#00024c', 
                fontSize: 28,
                transition: 'all 0.3s ease-in-out',
              }} /> */}
            </Box>
          )}

          {/* Collapse/Expand Button for Desktop */}
          {!isMobile && (
            <IconButton
              onClick={onCollapseToggle}
              sx={{
                color: "#fafafaff",
                backgroundColor: "#1959b9ff",
                transition: "all 0.3s ease-in-out",
                "&:hover": {
                  backgroundColor: "#1959b934",
                  color: "#ffffffff",
                },
              }}
            >
              {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          )}

          {/* Close Button for Mobile */}
          {isMobile && (
            <IconButton onClick={onClose} sx={{ color: "#00024c" }}>
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* medical Navigation Items */}
      <Box
        sx={{
          p: collapsed ? 1 : 2,
          borderBottom: "1px solid #e0e0e0",
          flexGrow: 1,
          overflowY: "auto",
          transition: "padding 0.3s ease-in-out",
        }}
      >
        <List sx={{ p: 0 }}>
          {MenuItems.filter((item) => {
            // ===== Assessor (userType 6) + capacity logic — must run BEFORE assessmentTypeId chain =====
            if (userTypeId === "6") {
              let isAllowedByCapacity = true;
              console.log('capacity in this condition', capacity);
              if (capacity) {
                const allowedSectionsByCapacity = {
                  1: [
                    "laboratory-detailsAssessment",
                    "lab-staffSectionAssessment",
                    "organization-assessment",
                  ],
                  2: ["disciplineAssessment", "lab-staffSectionAssessment"],
                  4: ["disciplineAssessment", "lab-staffSectionAssessment"],
                  3: [
                    "laboratory-detailsAssessment",
                    "lab-staffSectionAssessment",
                    "disciplineAssessment",
                    "organization-assessment",
                  ],
                };
                const allowedSections = allowedSectionsByCapacity[capacity];
                if (allowedSections) {
                  isAllowedByCapacity = allowedSections.includes(item.id);
                }
              }
              return item.requiredUserType6 === true && isAllowedByCapacity;
            }

            if (assessmentTypeId === 5) {
              return (
                item.id === "proficiency-testing" ||
                item.id === "discipline" ||
                item.id === "lab-staff" ||
                item.id === "enclosure" ||
                item.id === "fees" ||
                (item.id === "declaration" && showDeclaration) ||
                 (item.id === "final-submit-do" && shouldShowFinalSubmitOptions) ||
                (item.id === "final-submit-lab" && shouldShowFinaluserSubmitOptions) 
              );
            } else if (assessmentTypeId === 6 && !isSubUser) {
              return (
                item.id === "laboratory-details" ||
                item.id === "proficiency-testing" ||
                item.id === "discipline" ||
                item.id === "lab-staff" ||
                item.id === "enclosure" ||
                item.id === "non-analytical-instruments" ||
                item.id === "organization" ||
                (item.id === "final-submit-do" && shouldShowFinalSubmitOptions) ||
                (item.id === "final-submit-lab" && shouldShowFinaluserSubmitOptions) ||
                (item.id === "declaration" && showDeclaration)
              );
            } else if ((assessmentTypeId === 11 || assessmentTypeId === 12) && userTypeId !== "4") {
              return (item.id === "lab-staff-additional" ||
                item.id === "enclosure" ||
                item.id === "fees" ||
                (item.id === "declaration-additional" && showDeclaration) ||
                (item.id === "final-submit-additional" &&
                  shouldShowFinaluserSubmitOptionsadditionalLab)
              );
            } else if ((assessmentTypeId === 11 || assessmentTypeId === 12) && userTypeId === "4") {
              return (item.id === "lab-staff-additional" ||
                item.id === "enclosure" ||
                item.id === "fees" ||
                // (item.id === "declaration-additional" && showDeclaration) ||
                (item.id === "final-submit-additional" &&
                  shouldShowFinaluserSubmitOptionsadditional)
              );
            }
            else if (assessmentTypeId === 13 || assessmentTypeId === 17) {
              return (item.id === "discipline" ||
                item.id === "enclosure" ||
                (item.id === "fees" && assessmentTypeId === 13) ||
                (item.id === "declaration" && showDeclaration) ||
                (item.id === "final-submit-additional" &&
                  shouldShowFinaluserSubmitOptionsadditionalLab)
              );
            } else if ((assessmentTypeId === 13 || assessmentTypeId === 17 || assessmentTypeId === 11 || assessmentTypeId === 5 || assessmentTypeId === 12) && userTypeId === '4') {
              return (
                item.id === "final-submit-additional"
              );
            }
            // If user is a sub-user, only show Discipline and Proficiency Testing
            if (isSubUser) {
              return (
                item.id === "discipline" || item.id === "proficiency-testing"
              );
            }

            // ===== UserType 4 and other user types logic =====
            // For userTypeId 4 and others, exclude items that require userTypeId 6
            if (item.requiredUserType6 === true && userTypeId !== "6") {
              console.log(
                `🚫 Hiding assessment section "${item.id}" for userTypeId ${userTypeId}`,
              );
              return false; // Hide assessment sections for non-assessor users
            }

            // For regular users, apply existing conditional logic
            if (!item.conditional) return true;
            if (item.id === "declaration") {
              return showDeclaration;
            }
            if (item.id === "final-submit-do") {
              return shouldShowFinalSubmitOptions;
            }
            // if (item.id === "final-submit-additional") {
            //   return shouldShowFinalSubmitOptions;
            // }
            if (item.id === "final-submit-lab") {
              return shouldShowFinaluserSubmitOptions;
            }
            if (item.id === "back-to-dashboard") {
              return shouldShowFinaluserSubmitOptions;
            }
            if (item.id === "back-to-admin-dashboard") {
              return shouldShowFinalSubmitOptions;
            }
            if (item.requiresStage18UserType4) {
              return shouldShowFinalSubmitOptions;
            }
            if (item.requiresStage18a) {
              return shouldShowFinaluserSubmitOptions;
            }
            if (item.requiresStage18aadditional) {
              return shouldShowFinaluserSubmitOptionsadditional;
            }
            return false;
          }).map((item, index) => {
            const isActive = location.pathname === item.path;
            const listItemContent = (
              <ListItemButton
                onClick={() => handleSectionClick(item, index)}
                selected={isActive}
                sx={{
                  borderRadius: 1,
                  justifyContent: collapsed ? "center" : "flex-start",
                  px: collapsed ? 1 : 2,
                  transition: "all 0.3s ease-in-out",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                    transform: "translateX(2px)",
                  },
                  "&.Mui-selected": {
                    backgroundColor: "#e3f2fd",
                    "&:hover": {
                      backgroundColor: "#bbdefb",
                    },
                  },
                  py: 1,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: collapsed ? "unset" : 35,
                    justifyContent: "center",
                    transition: "all 0.3s ease-in-out",
                  }}
                >
                  <item.icon
                    sx={{
                      fontSize: collapsed ? 24 : 18,
                      color: item.completed
                        ? "#4caf50"
                        : isActive
                          ? "#1976d2"
                          : "#666",
                      transition: "all 0.3s ease-in-out",
                    }}
                  />
                </ListItemIcon>
                {!collapsed && (
                  <>
                    <ListItemText
                      primary={item.title}
                      sx={{
                        opacity: collapsed ? 0 : 1,
                        transition: "opacity 0.3s ease-in-out",
                        "& .MuiListItemText-primary": {
                          fontSize: "0.8rem",
                          fontWeight: isActive ? 600 : 500,
                          color: isActive ? "#1976d2" : "text.primary",
                          transition: "all 0.3s ease-in-out",
                        },
                      }}
                    />
                    {item.completed && (
                      <VerifiedIcon
                        sx={{
                          color: "#4caf50",
                          fontSize: 16,
                          opacity: collapsed ? 0 : 1,
                          transition: "opacity 0.3s ease-in-out",
                        }}
                      />
                    )}
                  </>
                )}
              </ListItemButton>
            );

            return (
              <ListItem key={index} disablePadding sx={{ mb: 0.5 }}>
                <PageTitle title="NABL | Application Form" />
                {collapsed ? (
                  <Tooltip title={item.title} placement="right" arrow>
                    {listItemContent}
                  </Tooltip>
                ) : (
                  listItemContent
                )}
              </ListItem>
            );
          })}
        </List>
      </Box>

      {/* Application Countdown Section */}
      {currentParams.appId &&
        (applicationStage === null ||
          applicationStage === "" ||
          applicationStage === undefined) && (
          <ApplicationCountdown
            appId={currentParams.appId}
            orgId={currentParams.orgId}
            labTypeId={currentParams.labTypeId}
            collapsed={collapsed}
            onDataLoaded={handleCountdownDataLoaded}
            showDetails={!isSubUser} // Hide details for sub-users
          />
        )}
    </>
  );
};

export default Sidebar;
